import { useRef } from 'react'
import { BG_PRESETS, type VBgMode } from './useVirtualBackground'
import { createLogger } from '../lib/logger'

const slog = createLogger('visio-vbg')

interface VirtualBgMenuProps {
  activeId: string
  onSelect: (id: string) => void
  onCustomImage: (file: File) => void | Promise<void>
  onClearCustom: () => void
  customSrc: string | null
  ready: boolean
  fps: number
  degraded: boolean
  uploadError?: string | null
}

export function VirtualBgMenu({
  activeId, onSelect, onCustomImage, onClearCustom, customSrc, ready, fps, degraded, uploadError,
}: VirtualBgMenuProps) {
  const fileRef = useRef<HTMLInputElement>(null)

  return (
    <div className="space-y-2">
      <div className="text-[10px] text-zinc-500 uppercase tracking-wide">Arriere-plan virtuel</div>

      {/* Preset grid */}
      <div className="grid grid-cols-3 gap-1.5">
        {BG_PRESETS.map(p => (
          <button
            key={p.id}
            onClick={() => { slog.action('vbg-select', { id: p.id, mode: p.mode }); onSelect(p.id) }}
            className={`flex flex-col items-center gap-0.5 p-1.5 rounded border text-[10px] transition-colors ${
              activeId === p.id
                ? 'border-blue-500 bg-blue-600/20 text-blue-300'
                : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600'
            }`}
          >
            {p.mode === 'none' ? (
              <div className="w-full aspect-video rounded bg-zinc-700 flex items-center justify-center text-zinc-500">Off</div>
            ) : p.mode === 'blur-light' || p.mode === 'blur-heavy' ? (
              <div className="w-full aspect-video rounded bg-zinc-700 flex items-center justify-center" style={{ filter: `blur(${p.mode === 'blur-light' ? 2 : 5}px)` }}>
                <div className="w-3 h-4 bg-zinc-400 rounded-t-full" />
              </div>
            ) : p.src ? (
              <img src={p.src} alt={p.label} className="w-full aspect-video rounded object-cover" />
            ) : null}
            <span className="truncate w-full text-center">{p.label}</span>
          </button>
        ))}

        {/* Custom upload */}
        <button
          onClick={() => {
            if (customSrc) { onSelect('custom') }
            else { fileRef.current?.click() }
          }}
          className={`flex flex-col items-center gap-0.5 p-1.5 rounded border text-[10px] transition-colors ${
            activeId === 'custom'
              ? 'border-blue-500 bg-blue-600/20 text-blue-300'
              : 'border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-zinc-600'
          }`}
        >
          {customSrc ? (
            <img src={customSrc} alt="Custom" className="w-full aspect-video rounded object-cover" />
          ) : (
            <div className="w-full aspect-video rounded bg-zinc-700 flex items-center justify-center text-zinc-500 text-lg">+</div>
          )}
          <span className="truncate w-full text-center">
            {customSrc ? 'Perso' : 'Ajouter'}
          </span>
        </button>
      </div>

      {/* Upload error toast (Magika reject 415) */}
      {uploadError && (
        <p className="text-[10px] text-red-400 px-1 leading-snug">{uploadError}</p>
      )}

      {/* Custom clear */}
      {customSrc && activeId === 'custom' && (
        <button
          onClick={onClearCustom}
          className="w-full text-[10px] text-red-400 hover:text-red-300 text-center py-0.5"
        >
          Supprimer l'image perso
        </button>
      )}

      {/* Status */}
      {activeId !== 'none' && (
        <div className="flex items-center justify-between text-[9px] text-zinc-500 px-1">
          <span>{ready ? 'Segmentation active' : 'Chargement...'}</span>
          {ready && (
            <span className={degraded ? 'text-yellow-500' : 'text-zinc-500'}>
              {fps > 0 ? `${fps} fps` : ''}
              {degraded ? ' (degrade)' : ''}
            </span>
          )}
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={e => {
          const f = e.target.files?.[0]
          if (f) { slog.action('vbg-custom-upload', { size: f.size }); onCustomImage(f) }
          e.target.value = ''
        }}
      />
    </div>
  )
}
